import React from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Breakpoints, Grid } from '../../app/StyleGuide';
import Breadcrumbs, { type Crumb } from '../../components/Breadcrumbs';
import { PrimaryButton } from '../../components/Buttons';
import EmptyState from '../../components/EmptyState';
import ErrorMessage from '../../components/ErrorMessage';
import { Container } from '../../components/Layout';
import LoadingState from '../../components/LoadingState';
import windowService from '../../services/windowService';
import { useUser } from '../authentication';
import AssessmentReportCategoryBreakdown from './components/AssessmentReportCategoryBreakdown';
import AssessmentReportHeader from './components/AssessmentReportHeader';
import AssessmentReportKeyInsights from './components/AssessmentReportKeyInsights';
import AssessmentReportOverallScore from './components/AssessmentReportOverallScore';
import AssessmentReportPrintable from './components/AssessmentReportPrintable';
import { useAssessmentResults } from './hooks/useAssessmentResults';
import { AssessmentReportViewModel } from './models/assessmentReportView';

const ScreenContent = styled.div`
  @media print {
    display: none;
  }
`;

export const AssessmentReportContent = styled(Container)`
  display: flex;
  flex-direction: column;
  gap: ${Grid._4};
  padding: ${Grid._8} ${Grid._4};

  @media (min-width: ${Breakpoints.screen_sm}) {
    gap: ${Grid._6};
    padding: ${Grid._8};
  }
`;

const crumbs: Crumb[] = [
  { name: 'Organization', route: '#/org' },
  { name: 'Assessment Results', route: '/#/assessment/results' },
  { name: 'Assessment Report' },
];

type AssessmentReportContentViewProps = {
  report: AssessmentReportViewModel;
};

const AssessmentReportContentView = ({ report }: AssessmentReportContentViewProps) => (
  <>
    <ScreenContent>
      <Breadcrumbs crumbs={crumbs} />
      <AssessmentReportHeader title={report.title} subtitle={report.subtitle} introduction={report.introduction} />
      <AssessmentReportContent>
        <AssessmentReportOverallScore score={report.overallScore} summary={report.overallSummary} />
        <AssessmentReportKeyInsights insights={report.insights} />
        <AssessmentReportCategoryBreakdown
          categories={report.categories}
          uncategorizedQuestions={report.uncategorizedQuestions}
        />
      </AssessmentReportContent>
    </ScreenContent>
    <AssessmentReportPrintable
      title={report.title}
      subtitle={report.subtitle}
      introduction={report.introduction}
      overallScore={report.overallScore}
      overallSummary={report.overallSummary}
      insights={report.insights}
      categories={report.categories}
      uncategorizedQuestions={report.uncategorizedQuestions}
    />
  </>
);

const ReportNotAvailableState = ({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children?: React.ReactNode;
}) => (
  <>
    <Breadcrumbs crumbs={crumbs} />
    <AssessmentReportContent>
      <EmptyState title={title} description={description}>
        {children}
        <PrimaryButton onClick={() => windowService.redirectTo('#/assessment/results')}>
          <i className="fa fa-chevron-left" /> Back to Assessment Results
        </PrimaryButton>
      </EmptyState>
    </AssessmentReportContent>
  </>
);

export const AssessmentResultsViewer = () => {
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const { user } = useUser();
  const orgId = user?.lastSelectedAccount;
  const reportState = useAssessmentResults(assessmentId, orgId);

  if (reportState.status === 'loading') {
    return (
      <>
        <Breadcrumbs crumbs={crumbs} />
        <AssessmentReportContent>
          <LoadingState />
        </AssessmentReportContent>
      </>
    );
  }

  if (reportState.status === 'error') {
    return (
      <>
        <Breadcrumbs crumbs={crumbs} />
        <AssessmentReportContent>
          <ErrorMessage>
            A problem occurred showing this page. Please refresh the page to try again. <a href="#/help">Contact Us</a>
          </ErrorMessage>
        </AssessmentReportContent>
      </>
    );
  }

  if (reportState.status === 'not_found') {
    return (
      <ReportNotAvailableState
        title="Assessment Not Available"
        description="No one in your organization has completed the assessment."
      />
    );
  }

  if (reportState.status === 'threshold_not_met') {
    return (
      <ReportNotAvailableState title={reportState.title} description={reportState.message}>
        <p>
          {reportState.respondentCount} of {reportState.minimumResponsesForAggregate} required responses completed.
        </p>
      </ReportNotAvailableState>
    );
  }

  return <AssessmentReportContentView report={reportState.report} />;
};

export default AssessmentResultsViewer;
